import React, { useState, useEffect } from 'react';
import { supabase, db } from '../lib/supabase';
import { printHtml } from '../lib/printHtml';

// ── Public Invoice Page — aspreo.com/invoice/{share_token} ───────────────────
// No auth needed. Loads invoice + business profile by share_token.
// Renders the same clean "invoice-box" design as the in-app preview,
// with a Download PDF button.

const FONT = "'Helvetica Neue',Helvetica,Arial,sans-serif";

export default function PublicInvoicePage() {
  const token    = window.location.pathname.split('/invoice/')[1];
  const [inv,    setInv]    = useState(null);
  const [biz,    setBiz]    = useState(null);
  const [loading,setLoading]= useState(true);
  const [error,  setError]  = useState('');

  useEffect(() => {
    if (!token) { setError('Invalid link.'); setLoading(false); return; }
    (async () => {
      // Try by share_token first, then by id as fallback
      const { data: invData, error: invErr } = await supabase
        .from('invoices')
        .select('*')
        .eq('share_token', token)
        .eq('is_shared', true)
        .maybeSingle();

      if (invErr) console.warn('invoice fetch:', invErr);
      if (!invData) { setError('This link is invalid or has expired.'); setLoading(false); return; }
      if (invData.share_expires_at && new Date(invData.share_expires_at) < new Date()) {
        setError('This link has expired.'); setLoading(false); return;
      }
      setInv(invData);
      // profiles' own RLS only allows a user to read their own row — an
      // unauthenticated visitor's join would silently return nothing, which
      // is why the logo/company name used to fall back to generic placeholder
      // text. public_business_profile is a narrow view that's readable by
      // anon for accounts with an active share (see migration 023).
      const { data: bizData } = await supabase
        .from('public_business_profile')
        .select('biz_name,biz_tagline,biz_address,biz_email,biz_phone,biz_gstin,biz_logo_url,biz_bank_name,biz_bank_acc,biz_bank_ifsc,biz_bank_type,plan_id')
        .eq('id', invData.user_id)
        .maybeSingle();
      setBiz(bizData || {});
      if (invData.client_id) {
        const { data: cl } = await supabase
          .from('clients').select('name,company,email,address,gstin')
          .eq('id', invData.client_id).maybeSingle();
        setInv(p => ({ ...p, _client: cl }));
      }
      setLoading(false);
    })();
  }, [token]);

  const printPDF = () => {
    printHtml(`<!DOCTYPE html><html><head><title>${inv?.invoice_number || 'Invoice'}</title><style>body{margin:0;font-family:${FONT}}@media print{.no-print{display:none}}</style></head><body>${document.getElementById('inv-print-area').innerHTML}</body></html>`);
  };

  if (loading) return (
    <div style={{minHeight:'100vh',background:'#F6F6F8',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:FONT}}>
      <div style={{textAlign:'center'}}>
        <div style={{width:44,height:44,border:'3px solid #2B1DC9',borderTopColor:'transparent',borderRadius:'50%',animation:'spin .8s linear infinite',margin:'0 auto 12px'}}/>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{color:'#4A4A6A'}}>Loading invoice…</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{minHeight:'100vh',background:'#F6F6F8',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:FONT,padding:24}}>
      <div style={{background:'#fff',borderRadius:16,padding:48,textAlign:'center',maxWidth:400,boxShadow:'0 8px 32px rgba(0,0,0,.08)'}}>
        <div style={{fontSize:48,marginBottom:16}}>🔗</div>
        <div style={{fontSize:18,fontWeight:700,marginBottom:8}}>Link Unavailable</div>
        <div style={{color:'#4A4A6A',fontSize:14,marginBottom:24}}>{error}</div>
        <a href="https://www.aspreo.com" style={{color:'#2B1DC9',fontWeight:700,textDecoration:'none'}}>← aspreo.com</a>
      </div>
    </div>
  );

  const items    = inv.items || [];
  const subtotal = items.reduce((s,i)=>s+(parseFloat(i.amount)||0),0);
  const taxAmt   = subtotal*((parseFloat(inv.tax_pct)||0)/100);
  const total    = subtotal+taxAmt;
  const client   = inv._client;
  const isProforma = inv.doc_type === 'proforma';
  const docLabel = isProforma ? 'Proforma Invoice' : 'Invoice';
  const hasBank  = biz?.biz_bank_acc || biz?.biz_bank_name;

  return (
    <div style={{minHeight:'100vh',background:'#F6F6F8',fontFamily:FONT,padding:'32px 16px'}}>
      {/* Toolbar */}
      <div className="no-print" style={{maxWidth:800,margin:'0 auto 16px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div style={{fontSize:13,color:'#8888AA'}}>{docLabel} shared via <strong style={{color:'#2B1DC9'}}>Aspreo</strong></div>
        <button onClick={printPDF} style={{background:'#2B1DC9',color:'#fff',border:'none',borderRadius:9,padding:'10px 20px',cursor:'pointer',fontFamily:FONT,fontSize:14,fontWeight:700,boxShadow:'0 4px 12px #2B1DC944'}}>
          ⬇ Download PDF
        </button>
      </div>

      {/* Invoice — same clean "invoice-box" design as the in-app preview */}
      <div id="inv-print-area" className="invoice-box" style={{maxWidth:800,margin:'0 auto',padding:30,border:'1px solid #eee',boxShadow:'0 0 10px rgba(0,0,0,.15)',fontSize:14,lineHeight:1.6,fontFamily:FONT,color:'#555',background:'#fff'}}>
        {/* Top: logo/name + doc details */}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',paddingBottom:20,flexWrap:'wrap',gap:16}}>
          <div>
            {biz?.biz_logo_url
              ? <img src={biz.biz_logo_url} alt="logo" style={{maxWidth:220,maxHeight:60,objectFit:'contain'}}/>
              : <div style={{fontSize:32,lineHeight:'32px',color:'#333',fontWeight:700}}>{biz?.biz_name||'Business'}</div>}
          </div>
          <div style={{textAlign:'right',fontSize:13}}>
            {isProforma && <div style={{display:'inline-block',fontSize:10,fontWeight:700,letterSpacing:'.08em',color:'#A06014',background:'#F5EBD9',borderRadius:4,padding:'2px 8px',marginBottom:6}}>PROFORMA — NOT A TAX INVOICE</div>}
            <div>{docLabel} #: {inv.invoice_number||'—'}</div>
            <div>Created: {new Date(inv.created_at).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}</div>
            {inv.due_date&&<div>{isProforma?'Valid until':'Due'}: {inv.due_date}</div>}
          </div>
        </div>

        {/* From / To */}
        <div style={{display:'flex',justifyContent:'space-between',paddingBottom:30,flexWrap:'wrap',gap:20}}>
          <div style={{fontSize:13,lineHeight:1.6}}>
            <strong>{biz?.biz_name||'Business'}</strong><br/>
            {biz?.biz_address&&<>{biz.biz_address}<br/></>}
            {biz?.biz_email&&<>{biz.biz_email}<br/></>}
            {biz?.biz_gstin&&<>GSTIN: {biz.biz_gstin}</>}
          </div>
          <div style={{fontSize:13,lineHeight:1.6,textAlign:'right'}}>
            {client ? <>
              <strong>{client.name}</strong><br/>
              {client.company&&<>{client.company}<br/></>}
              {client.email&&<>{client.email}<br/></>}
              {client.gstin&&<>GSTIN: {client.gstin}</>}
            </> : <span style={{color:'#999'}}>—</span>}
          </div>
        </div>

        {/* Payment method row — only if bank details exist */}
        {hasBank && (
          <table style={{width:'100%',borderCollapse:'collapse',marginBottom:20}}>
            <thead><tr style={{background:'#eee',borderBottom:'1px solid #ddd'}}>
              <th style={{padding:'6px 5px',textAlign:'left',fontWeight:700,fontSize:13}}>Payment Method</th>
              <th style={{padding:'6px 5px',textAlign:'right',fontWeight:700,fontSize:13}}>Account</th>
            </tr></thead>
            <tbody><tr>
              <td style={{padding:'6px 5px'}}>{biz?.biz_bank_name||'Bank transfer'}</td>
              <td style={{padding:'6px 5px',textAlign:'right'}}>{biz?.biz_bank_acc}{biz?.biz_bank_ifsc?` · IFSC ${biz.biz_bank_ifsc}`:''}</td>
            </tr></tbody>
          </table>
        )}

        {/* Items */}
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead><tr style={{background:'#eee',borderBottom:'1px solid #ddd'}}>
            <th style={{padding:'6px 5px',textAlign:'left',fontWeight:700,fontSize:13}}>Item</th>
            <th style={{padding:'6px 5px',textAlign:'center',fontWeight:700,fontSize:13}}>Qty</th>
            <th style={{padding:'6px 5px',textAlign:'right',fontWeight:700,fontSize:13}}>Rate</th>
            <th style={{padding:'6px 5px',textAlign:'right',fontWeight:700,fontSize:13}}>Amount</th>
          </tr></thead>
          <tbody>
            {items.map((it,i)=>(
              <React.Fragment key={i}>
                <tr style={{borderBottom:(i===items.length-1 && !it.italic_desc)?'none':'1px solid #eee'}}>
                  <td style={{padding:'6px 5px'}}>{it.desc||<span style={{color:'#999'}}>—</span>}</td>
                  <td style={{padding:'6px 5px',textAlign:'center'}}>{it.qty||1}</td>
                  <td style={{padding:'6px 5px',textAlign:'right'}}>₹{parseFloat(it.rate||0).toLocaleString('en-IN')}</td>
                  <td style={{padding:'6px 5px',textAlign:'right'}}>₹{parseFloat(it.amount||0).toLocaleString('en-IN')}</td>
                </tr>
                {it.italic_desc&&<tr style={{borderBottom:i===items.length-1?'none':'1px solid #eee'}}>
                  <td colSpan={4} style={{padding:'0 5px 6px',fontSize:12,color:'#777',fontStyle:'italic'}}>{it.italic_desc}</td>
                </tr>}
              </React.Fragment>
            ))}
            <tr><td colSpan={3} style={{textAlign:'right',padding:'6px 5px',fontSize:13}}>Subtotal</td><td style={{textAlign:'right',padding:'6px 5px',fontSize:13}}>₹{subtotal.toLocaleString('en-IN')}</td></tr>
            {parseFloat(inv.tax_pct)>0&&<tr><td colSpan={3} style={{textAlign:'right',padding:'6px 5px',fontSize:13}}>GST ({inv.tax_pct}%)</td><td style={{textAlign:'right',padding:'6px 5px',fontSize:13}}>₹{taxAmt.toLocaleString('en-IN')}</td></tr>}
            <tr>
              <td colSpan={3} style={{textAlign:'right',padding:'8px 5px',borderTop:'2px solid #eee',fontWeight:700}}>Total:</td>
              <td style={{textAlign:'right',padding:'8px 5px',borderTop:'2px solid #eee',fontWeight:700}}>₹{total.toLocaleString('en-IN')}</td>
            </tr>
          </tbody>
        </table>

        {inv.notes&&<div style={{fontSize:13,marginTop:20}}><strong>Notes:</strong> {inv.notes}</div>}
        {inv.terms&&<div style={{fontSize:12,color:'#777',marginTop:inv.notes?8:20}}>{inv.terms}</div>}
        {isProforma&&<div style={{fontSize:11,color:'#A06014',marginTop:12,fontStyle:'italic'}}>This is a proforma invoice for reference only — it is not a demand for payment.</div>}
        {inv.share_expires_at&&(
          <div style={{fontSize:10,color:'#D97706',textAlign:'right',marginTop:10}}>
            Link expires: {new Date(inv.share_expires_at).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}
          </div>
        )}
        {(biz?.plan_id==="hobby"||!biz?.plan_id)&&(
          <div style={{fontSize:10,color:'#aaa',textAlign:'center',marginTop:16}}>Created with <strong>Aspreo</strong> · aspreo.com</div>
        )}
      </div>
    </div>
  );
}
